import { AsyncPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { AuthService } from '../../../auth';
import { ThemeMode } from '../../constants';
import { ThemeService } from '../../services';

/**
 * The Light / Dark / Match Windows options, shown inside the preferences menu.
 *
 * An app opts in with `[showAppearance]="true"` on `app-top-menu` — phase 1 is TmsWeb and TmsAdmin.
 * ⚠️ The dropdown's content stays in the DOM while closed, so this is always created with the
 * preferences menu — which is what makes `loadSavedAppearance()` run at sign-in.
 */
@Component({
  selector: 'app-appearance-menu',
  templateUrl: './appearance-menu.component.html',
  styleUrl: './appearance-menu.component.scss',
  imports: [AsyncPipe],
})
export class AppearanceMenuComponent implements OnInit {
  protected readonly themeService = inject(ThemeService);
  private readonly authService = inject(AuthService);

  protected readonly options = [
    { mode: ThemeMode.light, label: $localize`Light`, iconClass: 'fa-sun' },
    { mode: ThemeMode.dark, label: $localize`Dark`, iconClass: 'fa-moon' },
    { mode: ThemeMode.system, label: $localize`Match Windows`, iconClass: 'fa-desktop' },
  ];

  protected isAuthenticated$: Observable<boolean>;

  ngOnInit() {
    this.isAuthenticated$ = this.authService.isAuthenticated();
    this.loadSavedAppearance();
  }

  protected setAppearance(mode: ThemeMode) {
    // The screen changes immediately; the save only records it against the user for other PCs.
    this.themeService.setMode(mode).subscribe();
  }

  /**
   * The appearance saved against the user is read here rather than in the app initializer: the
   * request needs a token, and at start-up there may be no user yet. `take(1)` keeps it to the
   * first sign-in of the session, so nothing needs unsubscribing.
   */
  private loadSavedAppearance() {
    this.isAuthenticated$
      .pipe(
        filter((isAuthenticated: boolean) => isAuthenticated),
        take(1),
        switchMap(() => this.themeService.load()),
      )
      .subscribe();
  }
}
